import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Settings2, ImageIcon, ShieldCheck, LogOut, Inbox, Users, BellRing, UserCog, ScrollText } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspaceBranding } from "../contexts/WorkspaceBrandingContext";
import api from "../lib/api";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";

const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
    { to: "/settings", label: "Business data", icon: Settings2, testid: "nav-settings" },
    { to: "/photos", label: "Photos & requests", icon: ImageIcon, testid: "nav-photos" },
    { to: "/account", label: "Account & privacy", icon: UserCog, testid: "nav-account" },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { businessName } = useWorkspaceBranding();
    const { requestAction } = useNavigationGuard();
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        if (user?.role !== "admin") return;
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const { data } = await api.get("/admin/change-requests/pending-count");
                if (!cancelled) setPendingCount(Number(data?.count) || 0);
            } catch (_e) {
                /* silent — badge just stays at last known value */
            }
        };
        fetchCount();
        // Refresh whenever the tab regains focus — cheap, no polling.
        const onFocus = () => fetchCount();
        window.addEventListener("focus", onFocus);
        window.addEventListener("webjesters:requests-changed", onFocus);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("webjesters:requests-changed", onFocus);
        };
    }, [user?.role]);

    return (
        <aside
            className="hidden md:flex h-screen w-64 shrink-0 flex-col border-r border-zinc-800/80 bg-[#0a0a0a] sticky top-0"
            data-testid="desktop-sidebar"
        >
            {/* Brand */}
            <div className="h-[76px] shrink-0 flex items-center px-6 border-b border-zinc-800/80">
                <Logo size={34} withSubtitle testid="sidebar-brand-logo" />
            </div>

            {/* Section label */}
            <div className="px-6 pt-6 pb-2">
                <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
                    Workspace
                </span>
            </div>

            {/* Nav */}
            <nav className="flex-1 min-h-0 overflow-y-auto px-3 pb-4 space-y-0.5" data-testid="sidebar-nav">
                {navItems.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                        key={to}
                        to={to}
                        data-testid={testid}
                        className={({ isActive }) =>
                            [
                                "group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors",
                                isActive
                                    ? "bg-zinc-900/80 text-zinc-50"
                                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50",
                            ].join(" ")
                        }
                    >
                        <Icon className="h-4 w-4 stroke-[1.75]" />
                        <span>{label}</span>
                    </NavLink>
                ))}

                {user?.role === "admin" && (
                    <>
                        <div className="px-3 pt-6 pb-2">
                            <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
                                WebJesters
                            </span>
                        </div>
                        <NavLink
                            to="/admin/inbox"
                            data-testid="nav-admin-inbox"
                            className={({ isActive }) =>
                                [
                                    "group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors",
                                    isActive
                                        ? "bg-zinc-900/80 text-zinc-50"
                                        : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50",
                                ].join(" ")
                            }
                        >
                            <BellRing className="h-4 w-4 stroke-[1.75]" />
                            <span className="flex-1">Inbox</span>
                        </NavLink>
                        <NavLink
                            to="/admin"
                            end
                            data-testid="nav-admin"
                            className={({ isActive }) =>
                                [
                                    "group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors",
                                    isActive
                                        ? "bg-zinc-900/80 text-zinc-50"
                                        : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50",
                                ].join(" ")
                            }
                        >
                            <ShieldCheck className="h-4 w-4 stroke-[1.75]" />
                            <span className="flex-1">Admin</span>
                            {pendingCount > 0 && (
                                <span
                                    data-testid="nav-admin-badge"
                                    title={`${pendingCount} unresolved request${pendingCount === 1 ? "" : "s"}`}
                                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-amber-500/15 text-amber-200/90 border border-amber-700/30 text-[10.5px] font-medium tabular-nums tracking-tight"
                                >
                                    {pendingCount > 99 ? "99+" : pendingCount}
                                </span>
                            )}
                        </NavLink>
                        <NavLink
                            to="/admin/clients"
                            data-testid="nav-admin-clients"
                            className={({ isActive }) =>
                                [
                                    "group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors",
                                    isActive
                                        ? "bg-zinc-900/80 text-zinc-50"
                                        : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50",
                                ].join(" ")
                            }
                        >
                            <Users className="h-4 w-4 stroke-[1.75]" />
                            <span className="flex-1">Clients</span>
                        </NavLink>
                        <NavLink
                            to="/admin/audit"
                            data-testid="nav-admin-audit"
                            className={({ isActive }) =>
                                [
                                    "group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors",
                                    isActive
                                        ? "bg-zinc-900/80 text-zinc-50"
                                        : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50",
                                ].join(" ")
                            }
                        >
                            <ScrollText className="h-4 w-4 stroke-[1.75]" />
                            <span className="flex-1">Audit log</span>
                        </NavLink>
                    </>
                )}
            </nav>

            {/* User block */}
            <div className="p-3 border-t border-zinc-800/80">
                <div className="px-3 py-2 rounded-md flex items-center gap-3" data-testid="sidebar-user">
                    <div className="h-7 w-7 shrink-0 rounded-full bg-zinc-800 flex items-center justify-center text-[11px] font-medium text-zinc-300">
                        {(user?.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-zinc-200 truncate" data-testid="sidebar-business-name">
                            {businessName}
                        </p>
                        <p className="text-[11.5px] text-zinc-500 truncate" data-testid="sidebar-user-email">
                            {user?.email}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => requestAction(logout)}
                    data-testid="sidebar-logout-button"
                    className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60 transition-colors"
                >
                    <LogOut className="h-4 w-4 stroke-[1.75]" />
                    Sign out
                </button>
            </div>
        </aside>
    );
}
